import {
	ChangeDetectionStrategy,
	Component,
	Input,
	Output,
	OnInit,
	OnChanges,
	SimpleChanges,
	EventEmitter,
	ChangeDetectorRef
} from "@angular/core";

import * as moment from 'moment';

import {
	LogCat,
	LogManager
} from "../../../log";

import {TranslateService} from "../../../../common/translate";

import {IDateRange} from "./date.range.picker.interface";

/*
* Selector: cp-date-range-picker
* Change Detection: OnPush
* View Encapsulation: Emulated
*
* Inputs:
* dateRange: IDateRange			//
* disabled: boolean				// Optional
*
* Outputs:
* open?: EventEmitter<any>		// Optional
* close?: EventEmitter<any>		// Optional
* onChange?: EventEmitter<any>	// Optional, for emitting event: {event: DOMEvent, dateRange: { from: Date, to: Date }}
* */
@Component({
	selector:'cp-date-range-picker',
	templateUrl: './date.range.picker.html',
	styleUrls: ['./date.range.picker.scss'],
	changeDetection:ChangeDetectionStrategy.OnPush
})
export class DateRangePickerComponent implements OnInit, OnChanges {

	@Input() dateRange: IDateRange;
	@Input() disabled: boolean = false;

	@Output() open: EventEmitter<any> = new EventEmitter<any>(false);
	@Output() close: EventEmitter<any> = new EventEmitter<any>(false);
	@Output() onChange: EventEmitter<IDateRange> = new EventEmitter<IDateRange>(false);

	public isDatePickerPopupOpened: boolean = false;
	public dictionary: any = {};

	private closeDatePickerPopupTimeout: any = null;

	constructor(private changeDetectorRef:ChangeDetectorRef,
				private translateService:TranslateService) {
		LogManager.debug(LogCat.RisWorklistCategories.DateFilter,"DateRangePickerComponent.constructor");
	}

	public ngOnChanges(changes: SimpleChanges){
		LogManager.debug(LogCat.RisWorklistCategories.DateFilter,"DateRangePickerComponent.ngOnChanges");
		this.updateDateRange();
	}

	public ngOnInit(){
		LogManager.debug(LogCat.RisWorklistCategories.DateFilter,"DateRangePickerComponent.ngOnInit");
		this.initDictionary();
	}

	/*
	* Open date range picker popup
	* */
	public openDatePickerPopup($event?: Event) {
		LogManager.debug(LogCat.RisWorklistCategories.DateFilter,"DateRangePickerComponent.openDatePickerPopup");
		clearTimeout(this.closeDatePickerPopupTimeout);
		if (this.isDatePickerPopupOpened) {
			this.closeDatePickerPopup();
		} else {
			this.isDatePickerPopupOpened = true;
			this.open.emit();
		}
	}

	/*
	* Close date range picker popup
	* */
	public closeDatePickerPopup($event?: Event) {
		LogManager.debug(LogCat.RisWorklistCategories.DateFilter,"DateRangePickerComponent.closeDatePickerPopup");
		clearTimeout(this.closeDatePickerPopupTimeout);
		if (this.isDatePickerPopupOpened) {
			this.isDatePickerPopupOpened = false;
			this.close.emit();
		}
	}

	/*
	* Close handler bound to cpClickOut for kendo-ui popup
	* */
	public closeDatePickerPopupOnClickOutSide($event?: Event) {
		LogManager.debug(LogCat.RisWorklistCategories.DateFilter,"DateRangePickerComponent.closeDatePickerPopupOnClickOutSide");
		this.closeDatePickerPopupTimeout = setTimeout(() => {
			this.closeDatePickerPopup();
			this.changeDetectorRef.detectChanges();
		}, 0);
	}

	/*
	* Handle On Calendar Selection Range Change
	* */
	public handleSelectionRangeChange($event: IDateRange) {
		LogManager.debug(LogCat.RisWorklistCategories.DateFilter,"DateRangePickerComponent.handleSelectionRangeChange");
		let momentFromDate = moment($event.from);
		let momentToDate = moment($event.to);
		this.dateRange.from = momentFromDate.toDate();
		this.dateRange.to = momentToDate.toDate();
		this.closeDatePickerPopup();
		this.onChange.emit(this.dateRange);
	}

	/*
	* Initialize component's dictionary
	* */
	private initDictionary() {
		LogManager.debug(LogCat.RisWorklistCategories.DateFilter,"DateRangePickerComponent.initDictionary");
		this.dictionary = {};
	}

	/*
	* Check if date range is valid
	* */
	private isValidDateRange(dateRange: IDateRange) {
		LogManager.debug(LogCat.RisWorklistCategories.DateFilter,"DateRangePickerCalendarComponent.isValidDateRange");
		if (dateRange &&
			dateRange instanceof Object &&
			dateRange.from &&
			moment.isDate(dateRange.from) &&
			dateRange.to &&
			moment.isDate(dateRange.to) &&
			moment(dateRange.to).isSameOrAfter(dateRange.from)) {
			return true;
		}
		return false;
	}

	/*
	* Set Default Date Range (from today till tomorrow)
	* */
	private setDefaultDateRange() {
		LogManager.debug(LogCat.RisWorklistCategories.DateFilter,"DateRangePickerCalendarComponent.setDefaultDateRange");
		let now = new Date;
		let todayMoment = moment(now).hours(0).minutes(0).seconds(0).milliseconds(0);
		let tomorrowMoment = moment(now).add(1, 'd').hours(23).minutes(59).seconds(59).milliseconds(999);
		this.dateRange = { from: todayMoment.toDate(), to: tomorrowMoment.toDate() };
	}

	/*
	* Update Date Range
	* */
	private updateDateRange() {
		LogManager.debug(LogCat.RisWorklistCategories.DateFilter,"DateRangePickerCalendarComponent.updateDateRange");
		if (!this.isValidDateRange(this.dateRange)) {
			this.setDefaultDateRange();
		}
	}
}

